import { useRef, useState } from "react";
import type { GroupWithMembers } from "../../types";
import { COMPLETED_GROUP_STATUS } from "../../types";
import { Button } from "../Button";
import { ModalShell } from "../ModalShell";

interface SendToSalesModalProps {
  group: GroupWithMembers;
  /** Other versions of this group on the same Opportunity that will become read-only. */
  otherVersionCount: number;
  onConfirm: () => Promise<void>;
  onClose: () => void;
  children?: never;
}

/** Confirms the end of underwriting work: results go to Sales and this version becomes the completed one. */
export function SendToSalesModal({ group, otherVersionCount, onConfirm, onClose }: SendToSalesModalProps) {
  const [isSending, setIsSending] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const cohortCount = group.members.filter((member) => member.memberType === "SpockCohort" && !member.isExcluded).length;

  const handleConfirm = async () => {
    setIsSending(true);
    try {
      await onConfirm();
    } finally {
      setIsSending(false);
    }
  };

  return (
    <ModalShell
      title="Send to Sales"
      description={`Underwriting is done on ${group.name}. Send the results to Sales?`}
      onClose={onClose}
      initialFocusRef={cancelRef}
      size="md"
      footer={
        <>
          <Button ref={cancelRef} variant="secondary" onClick={onClose} disabled={isSending}>
            Cancel
          </Button>
          <Button onClick={() => void handleConfirm()} isLoading={isSending}>
            Send to Sales
          </Button>
        </>
      }
    >
      <p className="mb-3 text-sm text-gray-700">This will:</p>
      <ul className="list-disc space-y-1.5 pl-5 text-sm text-gray-700">
        <li>{cohortCount > 0 ? `Combine the ${cohortCount} Spock Doe cohort${cohortCount === 1 ? "" : "s"} into the single Spock Doe member that Sales sees.` : "Send members as listed (no Spock Doe cohorts are active)."}</li>
        <li>Publish the census to Salesforce: each enrolled member with their total debits, plus RMM and Adjusted RMM, linked back to this version. Sales can untick members there to see how the group would rate; that never changes this group.</li>
        <li>Share no condition, rating, or medication detail with Sales.</li>
        <li>
          Set the Quote status to <strong>{COMPLETED_GROUP_STATUS}</strong>.
        </li>
        <li>Email {group.quoteOwnerName} that the prescreen is complete.</li>
        <li>
          Make version {group.version} read-only.{otherVersionCount > 0 ? ` The ${otherVersionCount} other version${otherVersionCount === 1 ? "" : "s"} of this group also become read-only; this one is the version of record.` : ""} If something changes later, Reopen creates version {group.version + 1}.
        </li>
      </ul>
    </ModalShell>
  );
}
